/**
 * `map`, drawn as an SVG — the renderer that ALWAYS works.
 *
 * **There is no raster.** A pre-rendered 2GIS image carries an api key in its
 * URL, and anything the browser fetches is one DevTools tab from being read, so
 * the canvas graph does not ship one at all. What arrives is `pins[]` plus an
 * optional `route`, and the picture is ours: an equirectangular projection over
 * the bounding box, which at the scale of one park is indistinguishable from a
 * proper one and needs no tiles, no key and no third-party host.
 *
 * A missing `route` is NORMAL — the 2GIS routing quota runs out long before the
 * static-map one, and pins with no line are a working map. The deep link is
 * keyless, so it is safe to print.
 *
 * **This is not a placeholder for the live map** (`MapLive.tsx`). It is what a
 * visitor sees when the bundle was built with no MapGL key, when the browser
 * cannot run WebGL, when `mapgl.2gis.com` is unreachable, and after a lost
 * WebGL context — and in every one of those it shows the same walk. `MapCard`
 * chooses between the two; nothing else here knows the other exists.
 */

import type { MapCardProps } from '../generated/schemas';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';

const W = 320;
const H = 220;
const PAD = 26;

/** Not «stops»: `me` is the visitor and an `entrance` is not a sight. */
const PIN_CLASS: Record<string, string> = {
  sight: 'map__dot--sight',
  entrance: 'map__dot--gate',
  arrival: 'map__dot--gate',
  break: 'map__dot--break',
  me: 'map__dot--me',
};

export default function MapSketch({ props }: { props: MapCardProps }) {
  const lats = props.pins.map((pin) => pin.lat);
  const lons = props.pins.map((pin) => pin.lon);
  const routePoints = (props.route?.points ?? []).filter(
    (point): point is number[] => Array.isArray(point) && point.length >= 2,
  );
  // The drawn line may run outside the pins' own box, so it counts towards it.
  for (const [lat, lon] of routePoints as [number, number][]) {
    lats.push(lat);
    lons.push(lon);
  }

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  // A single pin has no extent; give it one so it lands in the middle.
  const spanLat = Math.max(maxLat - minLat, 0.0008);
  const spanLon = Math.max(maxLon - minLon, 0.0016);

  const place = (lat: number, lon: number) => ({
    x: PAD + ((lon - minLon) / spanLon) * (W - PAD * 2),
    // Latitude grows northwards, y grows downwards.
    y: PAD + (1 - (lat - minLat) / spanLat) * (H - PAD * 2),
  });

  const pins = props.pins.map((pin) => ({ ...pin, ...place(pin.lat, pin.lon) }));
  const line =
    routePoints.length > 1
      ? routePoints.map(([lat, lon]) => place(lat!, lon!))
      : // No route from the API: join the numbered stops in their own order,
        // which is the walk the plan already committed to.
        pins.filter((pin) => typeof pin.n === 'number');

  return (
    <article className="card card--map">
      <header className="card__head">
        <h3 className="card__title">На карте</h3>
        <span className="card__meta">
          {props.pins.length} {props.pins.length === 1 ? 'точка' : 'точек'}
        </span>
      </header>
      <svg
        className="map"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Точки: ${props.pins.map((pin) => plain(pin.label)).join(', ')}`}
      >
        {line.length > 1 ? (
          <polyline
            className="map__line"
            points={line.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')}
          />
        ) : null}
        {pins.map((pin) => (
          <g key={pin.id} className="map__pin">
            <circle
              className={cn('map__dot', PIN_CLASS[pin.kind] ?? PIN_CLASS.sight)}
              cx={pin.x}
              cy={pin.y}
              r={11}
            />
            <text className="map__num" x={pin.x} y={pin.y + 4} textAnchor="middle">
              {typeof pin.n === 'number' ? pin.n : '•'}
            </text>
            <title>{plain(pin.label)}</title>
          </g>
        ))}
      </svg>
      {props.dgis_url ? (
        <footer className="card__actions">
          <a
            className="chip chip--link"
            href={props.dgis_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            Открыть в 2ГИС
          </a>
        </footer>
      ) : null}
    </article>
  );
}
